import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquareWarning, Loader2, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { STATUTS_RECLAMATION, Badge } from '../components/qualiopi/constantes';
import { dateFr } from '../components/qualiopi/shared';

const Formulaire = ({ onEnvoye }) => {
  const [objet, setObjet] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const envoyer = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post('/reclamations', { objet, description });
      toast.success(`Réclamation ${data.numero} envoyée. ${data.accuse_envoye ? 'Un accusé de réception vous a été envoyé par e-mail.' : ''}`);
      setObjet(''); setDescription('');
      onEnvoye();
    } catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible, réessayez"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={envoyer} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid="mes-reclamations-form">
      <h2 className="text-base md:text-lg font-display font-semibold text-[#0F172A]">Faire une réclamation</h2>
      <Input value={objet} onChange={(e) => setObjet(e.target.value)} placeholder="Objet (ex : planning, matériel, accompagnement…)" required data-testid="mes-reclamations-objet" />
      <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} placeholder="Décrivez la difficulté rencontrée : ce qui s'est passé, quand, et ce que vous attendez." required data-testid="mes-reclamations-description" />
      <Button type="submit" disabled={busy || !objet.trim() || !description.trim()} className="bg-[#8DC63F] text-[#0F172A] hover:bg-[#7AB534]" data-testid="mes-reclamations-envoyer">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Envoyer ma réclamation</>}
      </Button>
    </form>
  );
};

const MaReclamation = ({ r }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ma-reclamation-${r.reclamation_id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-xs font-semibold text-slate-500">{r.numero}</span>
      <Badge meta={STATUTS_RECLAMATION[r.statut]} testId={`ma-reclamation-${r.reclamation_id}-statut`} />
      <span className="text-xs text-slate-400">envoyée le {dateFr(r.created_at)}</span>
    </div>
    <p className="mt-2 text-sm font-semibold text-[#0F172A]">{r.objet}</p>
    <p className="whitespace-pre-line text-sm text-slate-600">{r.description}</p>
    {r.reponse && (
      <div className="mt-3 rounded-lg border-l-4 border-emerald-500 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid={`ma-reclamation-${r.reclamation_id}-reponse`}>
        <p className="mb-1 flex items-center gap-1 font-semibold"><CheckCircle2 className="h-4 w-4" /> Réponse du centre · {dateFr(r.reponse_envoyee_le)}</p>
        <p className="whitespace-pre-line">{r.reponse}</p>
      </div>
    )}
  </div>
);

const DEPOSANTS = ['stagiaire', 'tuteur', 'entreprise'];

export default function MesReclamations({ user }) {
  const [items, setItems] = useState(null);
  const deposant = DEPOSANTS.includes(user?.role);
  const charger = useCallback(() => axios.get('/reclamations/mes').then((r) => setItems(r.data)).catch(() => toast.error('Impossible de charger vos réclamations')), []);
  useEffect(() => { if (deposant) charger(); }, [charger, deposant]);
  if (user?.role === 'centre') return <Navigate to="/qualiopi?onglet=reclamations" replace />;
  if (!deposant) return <p className="py-12 text-center text-slate-500" data-testid="mes-reclamations-non-autorise">Les réclamations se déposent depuis les espaces stagiaire, tuteur et entreprise.</p>;
  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="mes-reclamations-page">
      <div>
        <h1 className="text-3xl font-display font-bold text-[#0F172A] flex items-center gap-3"><MessageSquareWarning className="h-8 w-8 text-[#8DC63F]" /> Réclamation</h1>
        <p className="text-slate-500 mt-1">
          Une difficulté, un mécontentement ? Écrivez-nous : vous recevez un accusé de réception, puis la réponse du centre ici et par e-mail.{' '}
          <a href="/procedures-reclamation" target="_blank" rel="noreferrer" className="font-medium text-[#5a8a1f] hover:underline" data-testid="mes-reclamations-procedure">Voir la procédure</a>
        </p>
      </div>
      <Formulaire onEnvoye={charger} />
      <section className="space-y-3">
        <h2 className="text-base md:text-lg font-display font-semibold text-[#0F172A]">Mes réclamations</h2>
        {!items ? <div className="flex justify-center py-8 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
          : items.length === 0 ? <p className="text-sm text-slate-400" data-testid="mes-reclamations-vide">Vous n'avez envoyé aucune réclamation.</p>
            : items.map((r) => <MaReclamation key={r.reclamation_id} r={r} />)}
      </section>
    </div>
  );
}
